import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Camera, CalendarCheck, Check } from "lucide-react";
import { toast } from "sonner";
import { api, eur, errMsg } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Uploader } from "@/components/ui-craft";
import { AiAssistant, AiImagePreview } from "@/components/Ai";
import { useAuth } from "@/context/AuthContext";
import { useT, localized } from "@/i18n";

export default function NewRequest() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { config } = useAuth();
  const { t, locale } = useT();
  const [service, setService] = useState(null);
  const [homes, setHomes] = useState([]);
  const [path, setPath] = useState(sp.get("path") === "inspection" ? "inspection" : null);
  const [form, setForm] = useState({ address: "", city: "", description: "", answers: {}, photo_ids: [], home_id: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/services/${slug}`).then((r) => setService(r.data)).catch(() => nav("/")); api.get("/homes").then((r) => setHomes(r.data)).catch(() => {}); }, [slug, nav]);
  const pickHome = (home_id) => { const h = homes.find((x) => x.id === home_id); setForm({ ...form, home_id, address: h ? h.address : form.address, city: h ? h.city : form.city }); };
  if (!service) return <Shell><p className="text-sm text-ink/50">{t("common.loading")}</p></Shell>;

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/requests", { service_id: service.id, path, ...form, home_id: form.home_id || null });
      toast.success(t("req.sent", { n: data.number }));
      nav(`/app/richieste/${data.id}`);
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  return (
    <Shell>
      <div className="max-w-3xl rise">
        <p className="eyebrow">{service.category} · {service.price_note || <>{t("common.from")} {eur(service.price_from)}/{service.unit}</>}</p>
        <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-bold">{localized(service, "name", locale)}</h1>
        <p className="mt-3 text-ink/60">{localized(service, "description", locale)}</p>

        {service.slug === "segnala-problema" && (
          <div className="mt-10"><AiAssistant onApply={(res, text) => {
            setForm((f) => ({ ...f, description: `${text}\n\n[Assistente AI · ${res.model}] Servizio suggerito: ${res.service_name} · urgenza ${res.urgency}. ${res.summary}` }));
            if (res.service_slug && res.service_slug !== "segnala-problema") toast.info(`Suggerito: ${res.service_name}. Puoi proseguire qui: il back office assegnerà il servizio giusto.`);
            toast.success("Suggerimento inserito nelle note della richiesta");
          }} /></div>
        )}

        <h2 className="mt-12 text-lg font-semibold">{t("req.howQuote")}</h2>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          {[["remote", Camera, t("req.remote"), t("req.remoteDesc")],
            ["inspection", CalendarCheck, t("req.inspection"), t("req.inspectionDesc", { fee: eur(config?.inspection_fee ?? 50) })]].map(([k, Icon, title, d]) => (
            <button key={k} type="button" data-testid={`path-${k}`} onClick={() => setPath(k)}
              className={`card-craft p-6 text-left transition-[border-color,transform] duration-200 hover:-translate-y-0.5 ${path === k ? "!border-terracotta ring-2 ring-terracotta/30" : ""}`}>
              <div className="flex items-center justify-between"><Icon className="text-terracotta" />{path === k && <Check size={18} className="text-terracotta" />}</div>
              <h3 className="mt-3 font-serif text-2xl font-semibold">{title}</h3><p className="mt-1 text-sm text-ink/60">{d}</p>
            </button>
          ))}
        </div>

        {path && (
          <form onSubmit={submit} className="mt-12 space-y-8 rise" data-testid="request-form">
            <div>
              <h2 className="text-lg font-semibold">{t("req.where")}</h2>
              {homes.length > 0 && <div className="mt-4"><label className="label-craft">{t("req.homeLabel")}</label><select data-testid="request-home-select" className="input-craft" value={form.home_id} onChange={(e) => pickHome(e.target.value)}><option value="">{t("req.noHome")}</option>{homes.map((h) => <option key={h.id} value={h.id}>{h.name} · {h.address}, {h.city}</option>)}</select></div>}
              <div className="mt-4 grid sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2"><label className="label-craft">{t("folder.address")}</label><input data-testid="request-address-input" className="input-craft" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                <div><label className="label-craft">{t("folder.city")}</label><input data-testid="request-city-input" className="input-craft" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
              </div>
            </div>
            <div>
              <h2 className="text-lg font-semibold">{t("req.tellMore")}</h2>
              <div className="mt-4 space-y-4">
                {service.questions.map((q) => (
                  <div key={q}><label className="label-craft">{q}</label><input data-testid={`answer-input-${service.questions.indexOf(q)}`} className="input-craft" value={form.answers[q] || ""} onChange={(e) => setForm({ ...form, answers: { ...form.answers, [q]: e.target.value } })} /></div>
                ))}
                <div><label className="label-craft">{t("req.notes")}</label><textarea data-testid="request-description-input" className="input-craft min-h-[100px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              </div>
            </div>
            <div>
              <h2 className="text-lg font-semibold">{t("req.photos")} {path === "remote" ? t("req.recommended") : t("req.optional")}</h2>
              <p className="text-sm text-ink/55 mt-1 mb-4">{t("req.photosHint")}</p>
              <Uploader value={form.photo_ids} onChange={(ids) => setForm({ ...form, photo_ids: ids })} />
              {service.area === "interni" && <div className="mt-4"><AiImagePreview onGenerated={(id) => setForm((f) => ({ ...f, photo_ids: [...f.photo_ids, id] }))} /></div>}
            </div>
            <div className="card-craft p-6 bg-linen">
              <p className="text-sm text-ink/70">{path === "inspection" ? t("req.afterInspection", { fee: eur(config?.inspection_fee ?? 50) }) : t("req.afterRemote")}</p>
              <button data-testid="request-submit-button" className="btn-primary mt-4" disabled={busy}>{busy ? t("req.sending") : t("req.submit")}</button>
            </div>
          </form>
        )}
      </div>
    </Shell>
  );
}
